import { Component } from '@theme/component';

const KEY = 'douuod-wishlist-v1';
const LIMIT = 100;
const validHandle = (handle) => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle);
const clean = (value) => Array.isArray(value) ? [...new Set(value.filter(validHandle))].slice(0, LIMIT) : [];
const changes = new EventTarget();
let handles = [];
let temporary = false;
function readSaved() {
  try { handles = clean(JSON.parse(localStorage.getItem(KEY) || '[]')); }
  catch { handles = []; temporary = true; }
}
readSaved();
const notify = () => changes.dispatchEvent(new Event('change'));
function persist(next) {
  handles = clean(next);
  try { localStorage.setItem(KEY, JSON.stringify(handles)); temporary = false; }
  catch { temporary = true; }
  notify();
}
function syncCounts() {
  document.querySelectorAll('[data-dw-wishlist-count]').forEach((node) => {
    node.textContent = String(handles.length);
    node.hidden = handles.length === 0;
  });
}
changes.addEventListener('change', syncCounts);
window.addEventListener('storage', (event) => {
  if (event.key !== KEY && event.key !== null) return;
  readSaved(); notify();
});
window.addEventListener('pageshow', () => { if (!temporary) readSaved(); notify(); });
document.addEventListener('shopify:section:load', syncCounts);

class WishlistToggle extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    changes.addEventListener('change', this.sync, { signal: this.controller.signal });
    this.sync();
  }
  updatedCallback() { super.updatedCallback(); this.sync(); }
  disconnectedCallback() { super.disconnectedCallback(); this.controller?.abort(); }
  sync = () => {
    const button = this.refs.button;
    if (!button) return;
    const saved = handles.includes(this.dataset.handle);
    button.setAttribute('aria-pressed', String(saved));
    button.setAttribute('aria-label', (saved ? button.dataset.remove : button.dataset.save) + ': ' + button.dataset.title);
    if (this.refs.label) this.refs.label.textContent = saved ? button.dataset.remove : button.dataset.save;
  };
  /** @param {MouseEvent} event */
  toggle(event) {
    event.preventDefault();
    event.stopPropagation();
    const handle = this.dataset.handle;
    if (!validHandle(handle)) return;
    const panel = document.querySelector('dw-wishlist-panel');
    const saved = handles.includes(handle);
    if (!saved && handles.length >= LIMIT) { panel?.announce('limit'); return; }
    persist(saved ? handles.filter((item) => item !== handle) : [...handles, handle]);
    panel?.announce(saved ? 'removed' : 'saved');
  }
}
class WishlistPanel extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    this.copy = JSON.parse(this.refs.copy.textContent);
    const signal = this.controller.signal;
    changes.addEventListener('change', this.onChange, { signal });
    this.refs.dialog.addEventListener('close', this.onClose, { signal });
    this.refs.dialog.addEventListener('click', (event) => {
      if (event.target !== this.refs.dialog) return;
      const rect = this.refs.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
    }, { signal });
    syncCounts();
  }
  disconnectedCallback() {
    this.close(); this.controller?.abort(); this.fetchController?.abort();
    super.disconnectedCallback();
  }
  announce(key) { this.refs.announcement.textContent = this.copy[key] || ''; }
  onChange = () => {
    this.refs.privacy.textContent = this.copy[temporary ? 'temporary' : 'privacy'];
    if (this.refs.dialog.open) this.render();
  };
  /** @param {MouseEvent} event */
  open(event) {
    event?.preventDefault();
    if (this.refs.dialog.open) return;
    this.opener = event?.target instanceof Element ? event.target.closest('button') : null;
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.refs.dialog.showModal();
    this.refs.privacy.textContent = this.copy[temporary ? 'temporary' : 'privacy'];
    this.render();
  }
  close() { if (this.refs.dialog?.open) this.refs.dialog.close(); }
  onClose = () => {
    this.fetchController?.abort();
    document.body.style.overflow = this.previousOverflow ?? '';
    if (this.opener?.isConnected) this.opener.focus();
  };
  async render() {
    const active = document.activeElement;
    const focusHandle = active?.closest('[data-saved-handle]')?.dataset.savedHandle;
    this.fetchController?.abort();
    const request = new AbortController();
    this.fetchController = request;
    const snapshot = [...handles].reverse();
    const { list, empty, status } = this.refs;
    list.replaceChildren();
    empty.hidden = snapshot.length > 0;
    status.textContent = snapshot.length ? this.copy.loading : '';
    const root = this.dataset.root.endsWith('/') ? this.dataset.root : this.dataset.root + '/';
    const rows = snapshot.map((handle) => {
      const fragment = this.refs.item.content.cloneNode(true);
      const row = fragment.querySelector('li');
      row.dataset.savedHandle = handle;
      row.querySelector('img').hidden = true;
      row.querySelector('.dw-wishlist-item__title').textContent = handle.replaceAll('-', ' ');
      row.querySelectorAll('a').forEach((a) => { a.href = root + 'products/' + handle; });
      const remove = row.querySelector('.dw-wishlist-item__remove');
      remove.addEventListener('click', () => {
        persist(handles.filter((item) => item !== handle));
        this.announce('removed');
        const nextButton = list.querySelector('.dw-wishlist-item__remove') || this.refs.closeButton;
        nextButton.focus();
      }, { signal: request.signal });
      list.append(fragment);
      return { handle, row };
    });
    if (focusHandle) {
      const current = rows.find((item) => item.handle === focusHandle);
      current?.row.querySelector('.dw-wishlist-item__remove').focus();
    }
    let cursor = 0;
    const load = async ({ handle, row }) => {
      try {
        const response = await fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { signal: request.signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (response.status === 404 || response.status === 410) {
          row.querySelector('.dw-wishlist-item__availability').textContent = this.copy.unavailable;
          row.querySelectorAll('a').forEach((a) => { a.removeAttribute('href'); });
          return;
        }
        if (!response.ok) throw Error('Product request failed');
        const product = await response.json();
        if (request.signal.aborted) return;
        const title = row.querySelector('.dw-wishlist-item__title');
        title.textContent = product.title;
        row.querySelector('.dw-wishlist-item__remove').setAttribute('aria-label', this.copy.remove + ': ' + product.title);
        const image = row.querySelector('img');
        if (product.featured_image) {
          const imageUrl = new URL(product.featured_image, location.origin);
          if (imageUrl.protocol === 'https:') { imageUrl.searchParams.set('width', '240'); image.src = imageUrl.href; image.alt = product.title; image.hidden = false; }
        } else row.querySelector('.dw-wishlist-item__image').hidden = true;
        const money = new Intl.NumberFormat(this.dataset.locale || 'it', { style: 'currency', currency: this.dataset.currency || 'EUR' });
        const price = row.querySelector('.dw-wishlist-item__price');
        price.textContent = money.format(product.price / 100);
        if (product.compare_at_price > product.price && !product.price_varies) {
          const old = document.createElement('s');
          old.textContent = money.format(product.compare_at_price / 100);
          price.append(' ', old);
        }
        row.querySelector('.dw-wishlist-item__availability').textContent = product.available ? '' : this.copy.soldout;
      } catch (error) {
        if (request.signal.aborted) return;
        row.querySelector('.dw-wishlist-item__availability').textContent = this.copy.error;
        const retry = row.querySelector('.dw-wishlist-item__retry');
        retry.hidden = false;
        retry.addEventListener('click', () => this.render(), { once: true, signal: request.signal });
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, rows.length) }, async () => {
      while (cursor < rows.length && !request.signal.aborted) await load(rows[cursor++]);
    }));
    if (!request.signal.aborted) status.textContent = '';
  }
}
if (!customElements.get('dw-wishlist-toggle')) customElements.define('dw-wishlist-toggle', WishlistToggle);
if (!customElements.get('dw-wishlist-panel')) customElements.define('dw-wishlist-panel', WishlistPanel);
document.documentElement.classList.add('dw-wishlist-ready');
syncCounts();

